import { useCallback, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { viewAsHeaders, getViewAs } from './viewAs';

// Use a same-origin relative URL so this works in both the built client
// (served by Express on :3001) and the CRA dev server (:3000 with proxy).
// Absolute URLs trigger CORS preflights that mask the real error as
// "Failed to fetch", which is exactly what we don't want in a debug tool.
const DEBUG_URL = '/api/deploy/debug';

/**
 * Dev-only "Why can't I see this app?" diagnostic panel.
 * Calls /api/deploy/debug with current auth + View As headers and shows the
 * server's full reasoning for every app: visible to me, owner, group-mismatch, etc.
 *
 * Mounted from the View As widget — opens as an overlay so you don't lose context.
 */
export default function DebugPanel({ onClose }) {
  const { oktaAuth } = useOktaAuth() || {};
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewAs, setLocalViewAs] = useState(getViewAs());

  const fetchDebug = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev mode */ }
      const res = await fetch(DEBUG_URL, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...viewAsHeaders(),
        },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setData(body);
    } catch (e) {
      // Surface the URL we tried so "Failed to fetch" doesn't hide the cause
      // (wrong port, blocked preflight, server down, etc.).
      console.error('[DebugPanel] fetch failed', DEBUG_URL, e);
      setError(`${e.message} — tried ${DEBUG_URL}`);
    } finally {
      setLoading(false);
    }
  }, [oktaAuth]);

  useEffect(() => { fetchDebug(); }, [fetchDebug]);
  useEffect(() => {
    const handler = (e) => { setLocalViewAs(e.detail); fetchDebug(); };
    window.addEventListener('lyric-view-as-changed', handler);
    return () => window.removeEventListener('lyric-view-as-changed', handler);
  }, [fetchDebug]);

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <span>🔍 Deploy Visibility Debug</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={fetchDebug} style={styles.refreshBtn} disabled={loading}>
              {loading ? '…' : '↻ Refresh'}
            </button>
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
        </div>

        {viewAs && (
          <div style={styles.viewAsTag}>
            👁 Impersonating <strong>{viewAs.email}</strong>
            {viewAs.groups?.length > 0 && (
              <span style={{ marginLeft: 8 }}>· groups: {viewAs.groups.join(', ')}</span>
            )}
          </div>
        )}

        {error && (
          <div style={styles.error}>⚠️ {error}</div>
        )}

        {data && (
          <>
            <div style={styles.summary}>
              <div><strong>Current user:</strong> {data.currentUser}</div>
              <div>
                <strong>Groups</strong>{' '}
                <span style={{ color: '#888', fontSize: 11 }}>({data.groupSource})</span>:{' '}
                {data.userGroups?.length ? data.userGroups.join(', ') : <em style={{ color: '#888' }}>none</em>}
              </div>
              <div style={{ marginTop: 4 }}>
                <strong>Apps:</strong> {data.visibleAppCount} visible / {data.totalApps} total
              </div>
            </div>

            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>App</th>
                  <th style={styles.th}>Visible</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Deployed By</th>
                  <th style={styles.th}>Allowed Groups</th>
                  <th style={styles.th}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.apps.length === 0 && (
                  <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#888' }}>
                    No apps registered on the server.
                  </td></tr>
                )}
                {data.apps.map((a) => (
                  <tr key={a.appName} style={a.visibleToMe ? {} : { opacity: 0.55 }}>
                    <td style={styles.td}><code>{a.appName}</code></td>
                    <td style={{ ...styles.td, color: a.visibleToMe ? '#4caf50' : '#ef5350', fontWeight: 600 }}>
                      {a.visibleToMe ? '✅' : '❌'}
                    </td>
                    <td style={styles.td}>{a.status}</td>
                    <td style={styles.td}>{a.deployedBy}</td>
                    <td style={styles.td}>
                      {a.allowedGroups.length ? a.allowedGroups.join(', ') : <em style={{ color: '#888' }}>everyone</em>}
                    </td>
                    <td style={{ ...styles.td, fontSize: 11, color: a.visibleToMe ? '#aaa' : '#ef9a9a' }}>
                      {a.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: 'pointer', color: '#888', fontSize: 12 }}>Raw JSON</summary>
              <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>
            </details>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
    zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    width: 'min(900px, 92vw)', maxHeight: '85vh', overflowY: 'auto',
    backgroundColor: '#1e1e2e', border: '1px solid #444', borderRadius: 8,
    padding: 18, color: '#eee', fontSize: 13,
    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 12, fontSize: 16, fontWeight: 600, color: '#ffb300',
  },
  refreshBtn: {
    padding: '4px 10px', fontSize: 12, border: '1px solid #555',
    borderRadius: 4, backgroundColor: '#2a2a3a', color: '#eee', cursor: 'pointer',
  },
  closeBtn: {
    background: 'none', border: 'none', color: '#aaa', cursor: 'pointer',
    fontSize: 18, padding: '0 4px',
  },
  viewAsTag: {
    backgroundColor: '#3a3020', color: '#ffd180', padding: '6px 10px',
    borderRadius: 4, fontSize: 12, marginBottom: 10, border: '1px solid #ffb300',
  },
  summary: {
    backgroundColor: '#0f0f1a', padding: 10, borderRadius: 4, marginBottom: 12,
    lineHeight: 1.6,
  },
  error: {
    backgroundColor: '#3a1a1a', color: '#ef9a9a', padding: 10, borderRadius: 4,
    marginBottom: 10, fontSize: 12,
  },
  table: {
    width: '100%', borderCollapse: 'collapse', fontSize: 12,
  },
  th: {
    textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid #444',
    color: '#aaa', fontWeight: 600, fontSize: 11, textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  td: {
    padding: '6px', borderBottom: '1px solid #2a2a3a', verticalAlign: 'top',
  },
  pre: {
    backgroundColor: '#0f0f1a', padding: 10, borderRadius: 4, fontSize: 11,
    overflowX: 'auto', color: '#bbb', marginTop: 6,
  },
};
